import type { Mixpanel } from '@/plugins/mixpanel'
import { readIsAuthenticated } from '@/analytics/properties'
import { PHOENIX_EVENT_CONTEXT } from './phoenixContext'
import { getPeopleProfileProperties } from './superProperties'
import { debounce } from 'lodash'


const peopleProfilePayload = () => {
  const profile = getPeopleProfileProperties()
  return {
    $email: profile.email,
    $first_name: profile.first_name,
    $last_name: profile.last_name,
    $phone: profile.phone,
    ...PHOENIX_EVENT_CONTEXT,
    tenant: profile.tenant,
    cognito_user_id: profile.cognito_user_id,
    brand: profile.brand,
    subscription_status: profile.subscription_status,
    account_plan_type: profile.account_plan_type,
    delivery_frequency: profile.delivery_frequency,
    dog_count: profile.dog_count,
    state: profile.state,
  }
}


/**
 * Identifies the user to Mixpanel and writes their People profile. Debounced (trailing) so a
 * burst of calls coalesces into a single write. The initial identify plus the vue-query cache
 * updates that follow the login preload, and the invalidation burst after a mutation, all
 * resolve to one `people.set`. Idempotent and data-driven: no-ops before login (no
 * `cognito_user_id` in the cache) and after logout (cache cleared), so it never writes to the
 * anonymous profile.
 */
export const identifyMixpanelUser = debounce((mixpanel: Mixpanel): void => {
  // Gate on the auth store, not just the cache: a failed token refresh clears the session
  // (and resets Mixpanel) without clearing the query cache, so a pending debounced call
  // could otherwise re-identify a logged-out user from stale profile data.
  if (!readIsAuthenticated()) return

  const profile = peopleProfilePayload()

  if (!profile.cognito_user_id) return

  mixpanel.identify(profile.cognito_user_id)
  mixpanel.people.set(profile)
}, 500)
